import { useTranslate } from '@refinedev/core';
import { Card, Flex, Tabs, Typography, theme } from 'antd';
import type { MonetizationCustomerDetailOut } from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';
import { StatCell } from '@/components/common/show/StatCell';
import { orDash } from '@/components/common/show/shared';
import { RevenueCatCustomerLink } from '@/components/monetization/RevenueCatLink';
import { CUSTOMER_SHOW_TABS } from '../constants';
import type { CustomerShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<CustomerShowTab, string> = {
  general: 'base.sections.basic_info',
  entitlements: 'monetization.customers.sections.entitlements',
  billing_periods: 'monetization.customers.sections.billing_periods',
  events: 'monetization.customers.sections.events',
  withdrawal_contracts: 'monetization.customers.sections.withdrawal_contracts',
};

interface CustomerShowHeaderProps {
  record: MonetizationCustomerDetailOut;
  activeTab: CustomerShowTab;
  onTabChange: (key: string) => void;
}

export const CustomerShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: CustomerShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  const heading =
    record.owner_email ??
    record.owner_username ??
    record.revenuecat_customer_id;

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Title
        level={3}
        style={{
          margin: 0,
          fontSize: token.fontSizeHeading3,
          overflowWrap: 'anywhere',
        }}
      >
        {heading}
      </Title>
      <Text
        type="secondary"
        style={{ display: 'block', marginTop: token.marginXXS }}
      >
        {`ID ${record.id}`}
      </Text>

      <Flex
        gap={token.marginXL + token.margin}
        style={{ marginTop: token.marginLG }}
        wrap
      >
        <StatCell label={t('monetization.customers.fields.account_id')}>
          <RevenueCatCustomerLink
            id={record.account_id}
            title={t('monetization.customers.actions.open_in_revenuecat')}
          />
        </StatCell>
        <StatCell label={t('monetization.customers.fields.user_id')}>
          {record.user_id != null ? (
            <ShowLink resource="iam.users" id={record.user_id} />
          ) : (
            orDash(null)
          )}
        </StatCell>
        <StatCell label={t('monetization.customers.fields.owner_username')}>
          {orDash(record.owner_username)}
        </StatCell>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={CUSTOMER_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
